/*
 * MCRO001-F0050 / T-MCRO-050 (design: choreo MCRO001-D004 §§4.1.2/4.1.3/3, commit 84538abd)
 * Slice 1 of 3 — the inline annotation-box lexer + serializer.
 *
 * GRAMMAR (owner-final, tightened 2026-08-06):
 *   box   := "[!!" ( "-" | <kind> ":" <title>? ) <body> "-!!]"
 *
 *   - OPEN  = "[!!"   CLOSE = "-!!]"  (asymmetric; close matched by NESTING DEPTH).
 *   - Immediately after OPEN a MANDATORY marker: "-" (canonical PLAIN box) or
 *     "<kind>:" (TYPED box). There is no "header-omitted" state.
 *   - PLAIN box  [!!- body -!!]   -> no kind, no title; body is everything after "-".
 *   - TYPED box  [!!kind: title\n body -!!]:
 *        title = text up to the FIRST NEWLINE (empty allowed, single-line only);
 *        body  = everything AFTER that newline up to the depth-matched CLOSE.
 *        A typed box with NO newline before CLOSE  ->  title-only, empty body (edge b).
 *   - kind is an OPEN identifier (IDENT), never an enumerated set: an unknown kind is a
 *     VALID node (edge: unknown-kind), distinct from a MALFORMED box.
 *   - ESCAPE: "\[!!", "\-!!]", "\\" inside content produce the literal delimiter/backslash
 *     and keep the depth counter correct.
 *   - MALFORMED / unclosed: on unbalanced depth (or an unparseable header) the box degrades
 *     to LITERAL TEXT from the unmatched OPEN onward, and lexing RESUMES right after that
 *     OPEN — it never consumes the rest of the document.
 *
 * REAL TOKENIZER: a SINGLE-PASS depth-counting lexer (design §10.2). The document is
 * scanned strictly left-to-right ONCE. An explicit stack of open-box frames tracks
 * nesting depth: an OPEN with a valid header pushes a frame, a depth-matched CLOSE pops
 * it into a node. On end-of-input with unbalanced depth every unmatched OPEN degrades to
 * LITERAL text (its already-scanned content — text and any well-formed inner boxes that
 * DID close — is spliced back into the parent in order). There is NO backtracking and NO
 * re-parse of the tail: the scan index never moves backwards, so lexing is LINEAR in the
 * input length regardless of nesting depth. (The prior recursive-descent parseBox/parseBody
 * re-scanned the tail once per enclosing level when a nested box failed to close, which was
 * O(2^depth) — a DoS: ~30 nested unclosed "[!!- " froze the client for days. See T-MCRO-050
 * DoS regression test.) A defense-in-depth depth cap degrades to literal (never throws).
 *
 * PURE: lex and serialize are deterministic functions of their inputs — no clock,
 * no rng, no I/O.
 */

import { createAnnotationNode } from './annotationNode';

const OPEN = '[!!';
const CLOSE = '-!!]';
const ESC = '\\';
// Canonical field-value delimiter. A pipe never appears in OPEN/CLOSE, so the field
// parser and the box-depth parser never contend for the same character.
const FIELD_VALUE_DELIM = '|';

// Sentinel kind for a canonical PLAIN box `[!!- ... -!!]`. A plain box has no typed kind;
// we surface it as this constant so serialize/parse are symmetric.
export const PLAIN_KIND = '-';

// Field labels that map onto AnnotationNode's typed slots. Any other IDENT label is
// preserved as an attribute (forward-compatible, §4.4).
const TYPED_LABELS = new Set(['base', 'ours', 'theirs', 'body', 'id']);
const IDENT_RE = /^[A-Za-z_][A-Za-z0-9_-]*$/;

// Defense-in-depth cap on nesting. Well past any legitimate annotation nesting; a document
// that opens more boxes than this without closing them degrades the excess OPENs to literal
// text (it NEVER throws). The primary DoS defense is the single-pass scan below — this cap
// only bounds the frame stack's memory, it is not what makes the lexer linear-time.
const MAX_BOX_DEPTH = 256;

/**
 * @typedef {{ type: 'text', text: string }} TextToken
 */

/**
 * Lex `text` into a flat array of TextToken | AnnotationNode. Adjacent text is coalesced
 * into a single TextToken so the stream is canonical (needed for idempotency).
 *
 * @param {string} text
 * @returns {Array<TextToken|import('./annotationNode').AnnotationNode>}
 */
export function lex(text) {
  const n = text.length;
  // The frame stack. Index 0 is the SYNTHETIC top-level frame (depth 0, no header): its
  // `content` becomes the returned token stream. Every deeper entry is a real open box.
  const root = makeFrame(-1, -1, null, null, false);
  const stack = [root];
  const top = () => stack[stack.length - 1];

  let i = 0;
  while (i < n) {
    const frame = top();
    const inBox = stack.length > 1;

    // Escapes are literal in every context (OUTSIDE and inside a box body). Inside a box
    // the un-escaped delimiter is what lands in bodyText; the raw bytes land in `content`
    // so a later flatten reproduces the input exactly.
    if (text.startsWith(ESC + OPEN, i)) { pushText(frame, OPEN, text.slice(i, i + ESC.length + OPEN.length)); i += ESC.length + OPEN.length; continue; }
    if (text.startsWith(ESC + CLOSE, i)) { pushText(frame, CLOSE, text.slice(i, i + ESC.length + CLOSE.length)); i += ESC.length + CLOSE.length; continue; }
    if (text.startsWith(ESC + ESC, i)) { pushText(frame, ESC, text.slice(i, i + ESC.length * 2)); i += ESC.length * 2; continue; }

    // A depth-matched CLOSE pops the current box into a node (only when we are in a box).
    if (inBox && text.startsWith(CLOSE, i)) {
      const closed = stack.pop();
      const node = buildNode(closed.kind, closed.title, collectParsed(closed));
      pushNode(top(), node);
      i += CLOSE.length;
      continue;
    }

    // An OPEN may start a new box. Parse ONLY its header here (bounded, no recursion): if it
    // is well-formed we push a frame and keep scanning forward; if not, the OPEN is literal.
    if (text.startsWith(OPEN, i)) {
      const header = parseHeader(text, i);
      if (header && stack.length <= MAX_BOX_DEPTH) {
        const child = makeFrame(i, header.contentStart, header.kind, header.title, header.plain);
        // Edge (b): a single-line typed box (kind: title, no newline before CLOSE) is
        // title-only with an empty body — it has already consumed through its CLOSE.
        if (header.singleLine) {
          const node = buildNode(child.kind, child.title, collectParsed(child));
          pushNode(frame, node);
          i = header.end; // just past CLOSE
          continue;
        }
        stack.push(child);
        i = header.contentStart;
        continue;
      }
      // MALFORMED header (or depth cap hit): OPEN is literal, resume right after it.
      pushText(frame, OPEN, OPEN);
      i += OPEN.length;
      continue;
    }

    // Structured fields `label:|value|` are only recognised inside a TYPED box body.
    if (inBox && !frame.plain) {
      const field = tryParseField(text, i);
      if (field) {
        pushField(frame, field, text.slice(i, field.end));
        i = field.end;
        continue;
      }
    }

    // Any other character (including a stray CLOSE at top level) is literal.
    pushText(frame, text[i], text[i]);
    i += 1;
  }

  // EOF. Every frame above the root is UNBALANCED (unmatched OPEN). Degrade them to literal
  // in ONE forward pass: because a child frame is only pushed AFTER its parent's content up to
  // that point is complete, the correct linear order is simply root.content, then each deeper
  // frame's header + content in stack order. We append directly into root — no cascading
  // splice through intermediate frames — so flattening a deep unclosed chain stays LINEAR in
  // total content (not O(depth) copies of the tail).
  for (let d = 1; d < stack.length; d += 1) {
    flattenInto(root, stack[d], text);
  }

  return coalesce(root.content);
}

/**
 * Parse ONLY the header of a box beginning at `text[start]` (known to start with OPEN).
 * Returns { kind, title, plain, contentStart, singleLine, end } or null when the header is
 * malformed. Bounded and non-recursive: it never scans past this box's own header — the
 * body is handled by the single forward pass in `lex`. PURE.
 */
function parseHeader(text, start) {
  let i = start + OPEN.length;
  const n = text.length;
  if (i >= n) return null; // "[!!" at EOF -> malformed.

  if (text[i] === '-') {
    // PLAIN marker: body is everything after "-" up to the depth-matched CLOSE.
    return { kind: PLAIN_KIND, title: undefined, plain: true, contentStart: i + 1, singleLine: false, end: -1 };
  }

  // TYPED box: read IDENT kind up to ':'.
  const kindStart = i;
  while (i < n && text[i] !== ':' && text[i] !== '\n' && text[i] !== '\r' && !text.startsWith(OPEN, i) && !text.startsWith(CLOSE, i)) {
    i += 1;
  }
  if (i >= n || text[i] !== ':') return null; // no "<kind>:" and no "-" -> malformed.
  const kind = text.slice(kindStart, i);
  if (!IDENT_RE.test(kind)) return null;
  i += 1; // consume ':'

  // TITLE: text up to the first NEWLINE (single-line, empty allowed). A single-line typed box
  // ends the title at the depth-0 CLOSE with an empty body (edge b).
  const titleStart = i;
  let titleEnd = -1;
  let sawNewline = false;
  let closeAt = -1;
  while (i < n) {
    if (text.startsWith(ESC + CLOSE, i)) { i += ESC.length + CLOSE.length; continue; }
    if (text.startsWith(ESC + OPEN, i)) { i += ESC.length + OPEN.length; continue; }
    if (text.startsWith(ESC + ESC, i)) { i += ESC.length * 2; continue; }
    if (text.startsWith(CLOSE, i)) { titleEnd = i; closeAt = i; break; } // single-line typed (edge b)
    if (text[i] === '\n' || text[i] === '\r') {
      titleEnd = i;
      sawNewline = true;
      if (text[i] === '\r' && text[i + 1] === '\n') i += 2;
      else i += 1;
      break;
    }
    i += 1;
  }
  if (titleEnd === -1) return null; // ran off EOF with neither newline nor CLOSE.
  // Preserve title bytes exactly (round-trip fidelity, §10.2) — do NOT trim.
  const title = unescape(text.slice(titleStart, titleEnd));

  if (!sawNewline) {
    // Edge (b): single-line typed box, title-only, empty body. `closeAt` is the CLOSE.
    return { kind, title, plain: false, contentStart: closeAt, singleLine: true, end: closeAt + CLOSE.length };
  }
  return { kind, title, plain: false, contentStart: i, singleLine: false, end: -1 };
}

/**
 * A frame is one open box on the scan stack (or, at index 0, the synthetic top level).
 * `content` is the ordered, byte-exact record of everything scanned inside this box:
 * text runs (with both their un-escaped body form and their raw bytes), structured
 * fields, and closed child nodes. On a matched CLOSE we derive the node's bodyText /
 * fields / attrs / children from it; on EOF-unbalanced we replay it verbatim as literal.
 */
function makeFrame(openIndex, contentStart, kind, title, plain) {
  return { openIndex, contentStart, kind, title, plain, content: [] };
}

function pushText(frame, bodyForm, rawForm) {
  frame.content.push({ t: 'text', body: bodyForm, raw: rawForm });
}
function pushField(frame, field, rawForm) {
  frame.content.push({ t: 'field', label: field.label, value: field.value, raw: rawForm });
}
function pushNode(frame, node) {
  frame.content.push({ t: 'node', node });
}

/**
 * Derive the parsed pieces of a WELL-FORMED box from its frame content. Free text becomes
 * bodyText (preserved byte-for-byte, no trim — §10.2 round-trip); fields split into typed
 * slots vs. forward-compatible attributes; closed inner boxes become children. PURE.
 */
function collectParsed(frame) {
  const fields = {};
  const attrs = {};
  const children = [];
  let bodyText = '';
  for (const part of frame.content) {
    if (part.t === 'text') bodyText += part.body;
    else if (part.t === 'field') {
      if (TYPED_LABELS.has(part.label)) fields[part.label] = part.value;
      else attrs[part.label] = part.value;
    } else if (part.t === 'node') children.push(part.node);
  }
  return {
    fields,
    attrs: Object.keys(attrs).length ? attrs : undefined,
    children,
    bodyText,
  };
}

/**
 * Degrade an UNCLOSED frame to literal text inside its parent. Its OPEN and header bytes
 * become literal, then its scanned content is replayed in order: text/field runs re-emitted
 * as their exact raw bytes, closed inner boxes kept as nodes (they matched a CLOSE, so they
 * stay parsed — exactly what the old resume-after-OPEN behaviour reproduced). PURE.
 */
function flattenInto(parent, unclosed, text) {
  // Degrading this box is byte-identical to treating its OPEN as literal "[!!" and re-lexing
  // everything after it: escapes unescape, well-formed inner boxes stay nodes. We reproduce
  // that from what the forward scan already captured — no re-scan.
  //   OPEN literal + the box's HEADER bytes re-lexed as text (unescaped: a header that never
  //   completed a box is just literal text now).
  const openLiteral = text.slice(unclosed.openIndex, unclosed.openIndex + OPEN.length); // "[!!"
  const headerRaw = text.slice(unclosed.openIndex + OPEN.length, unclosed.contentStart);
  pushLiteral(parent, openLiteral + unescape(headerRaw));
  for (const part of unclosed.content) {
    if (part.t === 'node') pushNode(parent, part.node);
    // text/field runs: the literal (unescaped) form is what re-lexing the tail would produce.
    else pushLiteral(parent, literalOf(part));
  }
}

// The unescaped literal form of a content part — what re-lexing its raw bytes as plain text
// yields (escapes dropped). For fields the raw `label:|value|` bytes are literal once the
// box degrades, so unescape their raw form.
function literalOf(part) {
  return part.t === 'text' ? part.body : unescape(part.raw);
}

// Push a pre-computed literal string. Body and raw are identical (already unescaped): a later
// coalesce/flatten re-emits it verbatim, and re-lexing it is a fixed point.
function pushLiteral(frame, s) {
  if (s !== '') frame.content.push({ t: 'text', body: s, raw: s });
}

/**
 * Convert a frame's ordered content into the final flat token stream, coalescing adjacent
 * text into a single TextToken (canonical form, needed for idempotency). PURE.
 */
function coalesce(content) {
  const tokens = [];
  let buf = '';
  const flush = () => {
    if (buf !== '') { tokens.push({ type: 'text', text: buf }); buf = ''; }
  };
  for (const part of content) {
    if (part.t === 'node') { flush(); tokens.push(part.node); }
    // At top level, text/field runs surface as their literal (unescaped) form — an escaped
    // "\[!!" outside any box becomes the literal "[!!" (idempotent under serializeTokens).
    else buf += literalOf(part);
  }
  flush();
  return tokens;
}

/** Try to parse `IDENT:|value|` at `i`. Returns null if it does not match. PURE. */
function tryParseField(text, i) {
  const n = text.length;
  let j = i;
  const labelStart = j;
  while (j < n && /[A-Za-z0-9_-]/.test(text[j])) j += 1;
  if (j === labelStart) return null;
  const label = text.slice(labelStart, j);
  if (!IDENT_RE.test(label)) return null;
  if (text[j] !== ':' || text[j + 1] !== FIELD_VALUE_DELIM) return null;
  j += 2; // consume ":|"
  let value = '';
  while (j < n) {
    if (text[j] === ESC && j + 1 < n) {
      if (text.startsWith(ESC + CLOSE, j)) { value += CLOSE; j += ESC.length + CLOSE.length; continue; }
      if (text.startsWith(ESC + OPEN, j)) { value += OPEN; j += ESC.length + OPEN.length; continue; }
      const next = text[j + 1];
      value += next; // covers \| and \\ and any other escaped char
      j += 2;
      continue;
    }
    if (text[j] === FIELD_VALUE_DELIM) return { label, value, end: j + 1 };
    value += text[j];
    j += 1;
  }
  return null; // unterminated value -> not a field.
}

/** Build the final AnnotationNode from parsed pieces. PURE. */
function buildNode(kind, title, parsed) {
  const fields = parsed.fields || {};
  return createAnnotationNode({
    kind,
    id: fields.id || '',
    ...(title !== undefined ? { title } : {}),
    ...(fields.base !== undefined ? { base: fields.base } : {}),
    ...(fields.ours !== undefined ? { ours: fields.ours } : {}),
    ...(fields.theirs !== undefined ? { theirs: fields.theirs } : {}),
    ...(fields.body !== undefined
      ? { body: fields.body }
      : parsed.bodyText
        ? { body: parsed.bodyText }
        : {}),
    ...(parsed.attrs ? { attributes: parsed.attrs } : {}),
    ...(parsed.children && parsed.children.length ? { children: parsed.children } : {}),
  });
}

/** Unescape delimiter/backslash escapes in a raw slice. PURE. */
function unescape(raw) {
  let out = '';
  let i = 0;
  while (i < raw.length) {
    if (raw.startsWith(ESC + CLOSE, i)) { out += CLOSE; i += ESC.length + CLOSE.length; continue; }
    if (raw.startsWith(ESC + OPEN, i)) { out += OPEN; i += ESC.length + OPEN.length; continue; }
    if (raw.startsWith(ESC + ESC, i)) { out += ESC; i += ESC.length * 2; continue; }
    out += raw[i];
    i += 1;
  }
  return out;
}

/** Escape delimiter/backslash occurrences so serialized content round-trips. PURE. */
function escapeContent(value) {
  let out = '';
  let i = 0;
  const s = String(value);
  while (i < s.length) {
    if (s[i] === ESC) { out += ESC + ESC; i += 1; continue; }
    if (s.startsWith(OPEN, i)) { out += ESC + OPEN; i += OPEN.length; continue; }
    if (s.startsWith(CLOSE, i)) { out += ESC + CLOSE; i += CLOSE.length; continue; }
    out += s[i];
    i += 1;
  }
  return out;
}

/** Escape a field value (pipe escaping on top of content escaping). PURE. */
function escapeFieldValue(value) {
  return escapeContent(value).split(FIELD_VALUE_DELIM).join(ESC + FIELD_VALUE_DELIM);
}

/**
 * Serialize one AnnotationNode to its canonical box string. Round-trips with lex:
 * lex(serialize(node)) yields [node] (deep-equal). PURE.
 *
 * @param {import('./annotationNode').AnnotationNode} node
 * @returns {string}
 */
export function serialize(node) {
  if (!node || typeof node.kind !== 'string') {
    throw new Error('serialize: expected an AnnotationNode with a string `kind`.');
  }

  if (node.kind === PLAIN_KIND) {
    // Plain box: free-text body only. Emit "[!!-" + body + "-!!]" with NO cosmetic padding,
    // so parse recovers the body BYTE-FOR-BYTE (leading/trailing/only-whitespace preserved).
    // A single leading space is inserted ONLY when the body would otherwise start with a
    // character that changes the marker's meaning is unnecessary: the marker "-" is already
    // consumed, so the body begins immediately after it and round-trips exactly.
    const body = node.body !== undefined && node.body !== '' ? escapeContent(node.body) : '';
    return `${OPEN}-${body}${CLOSE}`;
  }

  // Typed box: [!!kind:title\n<fields><children>-!!]
  // Emitted with NO cosmetic whitespace. Fields (`label:|value|`), child boxes, and the
  // CLOSE are all self-delimiting, so no separators are needed — and any incidental space
  // WOULD leak into the free-text body on parse (typed body = text after the title newline),
  // forcing a lossy trim. The body is a delimited `body:|...|` field, so its exact bytes
  // (leading/trailing/only-whitespace) survive the round-trip. `parse(serialize(node))`
  // is therefore lossless with no trimming anywhere.
  let out = `${OPEN}${node.kind}:`;
  if (node.title !== undefined && node.title !== '') out += escapeContent(node.title);
  out += '\n';

  if (node.id) out += fieldStr('id', node.id);
  for (const label of ['base', 'ours', 'theirs']) {
    if (node[label] !== undefined) out += fieldStr(label, node[label]);
  }
  if (node.body !== undefined && node.body !== '') out += fieldStr('body', node.body);
  if (node.attributes) {
    for (const key of Object.keys(node.attributes)) out += fieldStr(key, node.attributes[key]);
  }
  if (node.children && node.children.length) out += node.children.map(serialize).join('');

  out += CLOSE;
  return out;
}

function fieldStr(label, value) {
  return `${label}:${FIELD_VALUE_DELIM}${escapeFieldValue(value)}${FIELD_VALUE_DELIM}`;
}

/**
 * Serialize a whole token stream back to text. Text tokens are re-emitted VERBATIM: a
 * bare OPEN/CLOSE that survived as literal text lexes back to the same literal text, so
 * emitting it verbatim keeps serialize(lex(x)) idempotent (an escaped form would re-lex
 * to the same literal but change bytes, breaking idempotency). PURE.
 *
 * @param {Array<TextToken|import('./annotationNode').AnnotationNode>} tokens
 * @returns {string}
 */
export function serializeTokens(tokens) {
  return tokens.map((t) => (t && t.type === 'text' ? t.text : serialize(t))).join('');
}
